// eslint-disable-next-line max-classes-per-file
import React from 'react';
import { useMutation } from '@tanstack/react-query';
import decodeJson from '@seccon/shared/utils/decodeJson';
import { ToastContext } from '@seccon/core/contexts/toast';
import CSRFContext from '@seccon/core/contexts/csrf';
import { EMPLOYEE_PORTAL } from '../constants';

class CreateError extends Error {}
class DocumentError extends Error {}
class NetworkError extends Error {
  constructor (message, reportIds) {
    super(message);
    this.reportIds = reportIds;
  }
}

function convertYesNoNAToBoolean (value) {
  return value === 'n/a' ? null : (value === 'yes');
}

const useCreateReport = () => {
  const toastApi = React.useContext(ToastContext);
  const csrfToken = React.useContext(CSRFContext);

  const handleCreateRequest = async (formData) => {
    try {
      const foreignTravel = {
        ...formData.foreignTravel,
        departureDate: formData.foreignTravel.departureDate.format('MM/DD/YYYY'),
        returnDate: formData.foreignTravel.returnDate.format('MM/DD/YYYY'),
        willUseForeignPassport: convertYesNoNAToBoolean(formData.foreignTravel.willUseForeignPassport),
        travelingPersonnel: {
          ...formData.foreignTravel.travelingPersonnel,
          id: formData.foreignTravel.travelingPersonnel.id.id,
          passport: {
            ...formData.foreignTravel.travelingPersonnel.passport,
            expirationDate: formData.foreignTravel.travelingPersonnel.passport.expirationDate.format('MM/DD/YYYY'),
            issueDate: formData.foreignTravel.travelingPersonnel.passport.issueDate.format('MM/DD/YYYY')
          }
        },
        destinations: formData.foreignTravel.destinations.map((destination) => ({
          ...destination,
          startDate: destination.startDate.format('MM/DD/YYYY'),
          endDate: destination.endDate.format('MM/DD/YYYY')
        }))
      };

      // Documents should not be included in the create report request
      delete foreignTravel.documents;

      const response = await fetch('/foreignTravel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'anti-csrf-token': csrfToken
        },
        body: JSON.stringify({ foreignTravel })
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${ response.status }`);
      }

      return await decodeJson(response);
    } catch (error) {
      console.error('Error creating report:', error);

      throw new CreateError('Unable to create foreign travel report.');
    }
  };

  const handleUploadDocuments = async (createResponse, documents) => {
    try {
      if (!documents || documents.length === 0) {
        console.log('No documents provided, skipping upload.');

        return;
      }

      let failedResponses = await Promise.all(
        createResponse.map((report) => {
          const reportId = report.foreignTravelId;

          const formData = new FormData();
          documents.forEach((doc) => {
            formData.append('foreignTravelDocument', doc);
          });

          return fetch(`/foreignTravel/${ reportId }/upload?origin=${ EMPLOYEE_PORTAL }`, {
            method: 'POST',
            headers: {
              'anti-csrf-token': csrfToken
            },
            body: formData
          }).then((resp) => (!resp.ok ? reportId : null))
        })
      );

      failedResponses = failedResponses.filter((resp) => !!resp);

      if (failedResponses.length > 0) {
        throw new NetworkError('Network response was not ok', failedResponses);
      }
    } catch (error) {
      console.error('Failed to upload foreign travel report', error);

      if (error instanceof NetworkError) {
        throw new DocumentError(`Report was created, however it failed to upload documents to some travel reports (${ error.reportIds.join(', ') }). Update those reports and try again.`);
      }

      throw new DocumentError('Report was created, however it failed to upload documents to the foreign travel reports. Update those report and try again.');
    }
  };

  const handleCreateSuccess = () => {
    toastApi.success('Successfully created foreign travel report');

    setTimeout(() => {
      window.location = '/employee/requestsAndReports';
    }, 3000);
  };

  const handleCreateError = (error) => {
    if (error instanceof CreateError) {
      toastApi.error(error.message);
    } else if (error instanceof DocumentError) {
      toastApi.warning(error.message);

      setTimeout(() => {
        window.location = '/employee/requestsAndReports';
      }, 3000);
    } else {
      toastApi.error('Unable to create foreign travel report.');
    }
  };

  const { mutateAsync: createReport, isPending: isSubmitting } = useMutation({
    mutationFn: async (formData) => {
      const createResponse = await handleCreateRequest(formData);

      console.log('---Foreign Travel is Created');

      await handleUploadDocuments(createResponse, formData.foreignTravel.documents);
    },
    onSuccess: handleCreateSuccess,
    onError: handleCreateError
  });

  return { createReport, isSubmitting };
};

export default useCreateReport;
